import { useEffect, useState } from 'react';
import { ButtonBack } from '../../components/ButtonBack';
import { Card } from '../../components/Card';
import { HelmetComponent } from '../../components/Helmet';
import { Loading } from '../../components/Loading';
import { useAuth } from '../../hooks/useAuth';
import { useQuiz } from '../../hooks/useQuiz';
import {
  IAvailableQuiz,
  IPlayerQuizPerformances,
} from '../../interfaces/IPlayerPerformance';
import { PointsOrigin } from './components/PointsOrigin';
import { QuizList } from './components/QuizList';

export function Trainings() {
  const { client, player } = useAuth();
  const { getPlayerPerformances } = useQuiz();
  const [quizPerformances, setQuizPerformances] = useState<
    IPlayerQuizPerformances | undefined
  >(undefined);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [filteredQuizzes, setFilteredQuizzes] = useState<
    (IAvailableQuiz & {
      accuracyPercentage: number;
      pointsEarned: number;
      awarded: boolean;
      nOfAttempts?: number;
    })[]
  >([]);

  useEffect(() => {
    const fetchPlayerData = async () => {
      if (player) {
        const result = await getPlayerPerformances(player.id);
        setQuizPerformances(result);
      }
    };

    fetchPlayerData();
  }, []);

  useEffect(() => {
    if (quizPerformances?.availableQuizzes) {
      let filtered = [...quizPerformances.availableQuizzes];

      // Filtro por nome
      if (searchTerm) {
        filtered = filtered.filter((quiz) =>
          quiz.actionTitle.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }

      // Filtro por status
      if (selectedStatus !== 'all') {
        const answeredIds =
          quizPerformances.answeredQuizzes?.map((q) => q.actionId) || [];
        const awardedIds =
          quizPerformances.awardedQuizzes?.map((q) => q.actionId) || [];

        switch (selectedStatus) {
          case 'not-answered':
            filtered = filtered.filter(
              (quiz) => !answeredIds.includes(quiz.actionId)
            );
            break;
          case 'answered':
            filtered = filtered.filter((quiz) =>
              answeredIds.includes(quiz.actionId)
            );
            break;
          case 'not-awarded':
            filtered = filtered.filter(
              (quiz) =>
                answeredIds.includes(quiz.actionId) &&
                !awardedIds.includes(quiz.actionId)
            );
            break;
          case 'awarded':
            filtered = filtered.filter((quiz) =>
              awardedIds.includes(quiz.actionId)
            );
            break;
        }
      }

      // Adiciona informações adicionais aos quizzes
      const enriched = filtered.map((quiz) => {
        const answeredQuiz = quizPerformances.answeredQuizzes?.find(
          (aq) => aq.actionId === quiz.actionId
        );
        const isAwarded = quizPerformances.awardedQuizzes?.some(
          (aq) => aq.actionId === quiz.actionId
        );

        return {
          ...quiz,
          accuracyPercentage: answeredQuiz?.accuracyPercentage || 0,
          pointsEarned: answeredQuiz?.pointsEarned || 0,
          awarded: isAwarded || false,
          hasAnswered: !!answeredQuiz,
          nOfAttempts: answeredQuiz?.nOfAttempts,
        };
      });

      setFilteredQuizzes(enriched);
    }
  }, [quizPerformances, searchTerm, selectedStatus]);

  if (!quizPerformances) {
    return <Loading />;
  }

  const answeredCount = quizPerformances.quizzesAnsweredCount || 0;
  const averageAllQuizzes = quizPerformances.averageAccuracy || 0;
  const overallAccuracyPercentage =
    quizPerformances.overallAccuracyPercentage || 0;
  const totalPoints = quizPerformances.totalPointsEarned || 0;

  return (
    <>
      <HelmetComponent
        clientLogoURL={client?.logoURL}
        companyName={client?.companyName}
        clientFavicon={client?.favIcon}
        primaryColor={client?.primaryColor}
        secondaryColor={client?.secondaryColor}
        tabTitle={'Meus Treinamentos'}
      />
      <div className="container">
        {/* Botão Voltar */}
        <div className="row">
          <div className="my-3">
            <ButtonBack title="Meus treinamentos" />
          </div>
        </div>

        {/* Layout principal: Estatísticas (1/4) | Conteúdo (3/4) */}
        <div className="row">
          {/* Coluna de Estatísticas - 1/4 */}
          <div className="col-sm-12 col-md-3">
            <div className="mb-3">
              <Card shadow>
                <div className="d-flex flex-column align-items-center justify-content-center py-2">
                  <h2 className="display-6 fw-bold text-dark mb-2">
                    {totalPoints}
                  </h2>
                  <p className="text-muted mb-0 text-center small">
                    Pontos Totais do Quiz
                  </p>
                </div>
              </Card>
            </div>
            <div className="mb-3">
              <Card shadow>
                <div className="d-flex flex-column align-items-center justify-content-center py-2">
                  <h2 className="display-6 fw-bold text-dark mb-2">
                    {Math.round(averageAllQuizzes)}%
                  </h2>
                  <p className="text-muted mb-0 text-center small">
                    Média de Aproveitamento
                  </p>
                </div>
              </Card>
            </div>
            <div className="mb-3">
              <Card shadow>
                <div className="d-flex flex-column align-items-center justify-content-center py-2">
                  <h2 className="display-6 fw-bold text-dark mb-2">
                    {answeredCount}
                  </h2>
                  <p className="text-muted mb-0 text-center small">
                    Quizes respondidos
                  </p>
                </div>
              </Card>
            </div>
            <div className="mb-3">
              <Card shadow>
                <div className="d-flex flex-column align-items-center justify-content-center py-2">
                  <h2 className="display-6 fw-bold text-dark mb-2">
                    {Math.round(overallAccuracyPercentage)}%
                  </h2>
                  <p className="text-muted mb-0 text-center small">
                    Média de todos os Quizes
                  </p>
                </div>
              </Card>
            </div>
          </div>

          {/* Coluna de Conteúdo - 3/4 */}
          <div className="col-sm-12 col-md-9">
            <PointsOrigin
              availableQuizzes={quizPerformances.availableQuizzes}
              answeredQuizzes={quizPerformances.answeredQuizzes}
            />

            <QuizList
              filteredQuizzes={filteredQuizzes}
              searchTerm={searchTerm}
              selectedStatus={selectedStatus}
              onSearchChange={setSearchTerm}
              onStatusChange={setSelectedStatus}
            />
          </div>
        </div>
      </div>

      {/* Estilos inline */}
      <style>{`
        .progress-bar {
          position: relative;
          overflow: visible;
        }

        .progress-bar span {
          line-height: 24px;
        }
      `}</style>
    </>
  );
}
